import { Autocomplete, Button, CircularProgress, TextField } from '@mui/material';
import { FC, UIEvent } from 'react';
import { t } from 'i18next';
import { RemoteMasterOption } from '../../types/Option';

interface RemotePaginatedSelectProps {
  id: string;
  label: string;
  options: RemoteMasterOption[];
  value: RemoteMasterOption | null;
  inputValue: string;
  loading: boolean;
  hasNextPage: boolean;
  editable: boolean;
  onChange: (option: RemoteMasterOption | null) => void;
  onInputChange: (value: string, reason: string) => void;
  onLoadMore: () => void;
  error?: boolean;
  onRetry?: () => void;
}

const RemotePaginatedSelect: FC<RemotePaginatedSelectProps> = ({
  id,
  label,
  options,
  value,
  inputValue,
  loading,
  hasNextPage,
  editable,
  onChange,
  onInputChange,
  onLoadMore,
  error = false,
  onRetry,
}) => {
  const handleScroll = (event: UIEvent<HTMLUListElement>) => {
    const listbox = event.currentTarget;
    const isNearBottom = listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 24;
    if (isNearBottom && hasNextPage && !loading && !error) onLoadMore();
  };

  return (
    <>
      <Autocomplete<RemoteMasterOption>
        id={id}
        options={options}
        value={value}
        inputValue={inputValue}
        disabled={!editable}
        loading={loading}
        openOnFocus
        filterOptions={(availableOptions) => availableOptions}
        isOptionEqualToValue={(option, selected) => option.code === selected.code}
        getOptionLabel={(option) => option.nombre}
        onChange={(_, option) => onChange(option)}
        onInputChange={(_, nextInputValue, reason) => onInputChange(nextInputValue, reason)}
        ListboxProps={{ onScroll: handleScroll }}
        noOptionsText={error ? t('administration.searchDialog.searchError') : t('administration.searchDialog.noResults')}
        loadingText={t('administration.searchDialog.loading')}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            variant="standard"
            sx={{
              '& .MuiInputLabel-root': { fontSize: 12 },
              '& .MuiInputLabel-shrink': { transform: 'translate(0, -2px) scale(1)' },
              '& .MuiInputBase-input': { fontWeight: 700, paddingTop: '18px', paddingBottom: '5px' },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {error && <div role="alert">
        <span>{t('administration.searchDialog.searchError')}</span>
        <Button disabled={loading} onClick={onRetry}>{t('administration.searchDialog.retry')}</Button>
      </div>}
    </>
  );
};

export default RemotePaginatedSelect;
